import { preparePythonPrediction } from "./python-prediction-request";
import { ProductCategory } from "@/core/common/types";
import { CustomModelTrainingStatus } from "@/core/common/types/custom-model-types";
import { Firestore } from "firebase/firestore";
import { Functions, httpsCallable, HttpsCallable } from "firebase/functions";
import { GenericBaseInput } from "../custom-model-post-process";
import { GenericBaseResponse } from "../image-generation-from-urls-types";
import { FirebaseBackend } from "./firebase-backend";
import { IMAGE_GENERATION_FROM_DND } from "./firebase-function-name";

export type RawPromptObject = {
  additionalPrompting: string;
};

export interface ImageGenerationFromDndRequest extends GenericBaseInput {
  imageUrls: string[];
  aspectRatio: string;
  promptObj: RawPromptObject;
  productCategory: ProductCategory;
  numOutputs?: number;
  width?: number;
  height?: number;
  publicTeamId?: string;
  predictionId?: string;
}

export interface ImageGenerationFromDndResponse extends GenericBaseResponse {
  predictionId?: string;
  output: string[];
}

export class ImageGenerationFromDndManager {
  private getCurrentTeamId: () => string | null | undefined;

  private firestore: Firestore;

  private backend: FirebaseBackend;

  private imageGenerationFromDnd: HttpsCallable<
    ImageGenerationFromDndRequest,
    ImageGenerationFromDndResponse
  >;

  constructor({
    firestore,
    firebaseFunctions,
    backend,
    getCurrentTeamId,
  }: {
    firestore: Firestore;
    firebaseFunctions: Functions;
    backend: FirebaseBackend;
    getCurrentTeamId: () => string | null | undefined;
  }) {
    this.firestore = firestore;
    this.backend = backend;
    this.getCurrentTeamId = getCurrentTeamId;

    this.imageGenerationFromDnd = httpsCallable(firebaseFunctions, IMAGE_GENERATION_FROM_DND, {
      timeout: 180000,
    });
  }

  async generateImageFromDnd(
    request: ImageGenerationFromDndRequest,
  ): Promise<ImageGenerationFromDndResponse> {
    const pending = await preparePythonPrediction(
      IMAGE_GENERATION_FROM_DND,
      request,
      this.getCurrentTeamId,
    );
    const predictionId = pending.request.predictionId;

    return new Promise((resolve, reject) => {
      let settled = false;
      let unsubscribe = () => {};
      const finish = (callback: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeout);
        unsubscribe();
        callback();
      };
      const timeout = setTimeout(
        () => finish(() => reject(new Error("Image generation timed out"))),
        1800000,
      );

      unsubscribe = this.backend.onCustomModelPredictionUpdate({
        predictionId,
        callback: (prediction) => {
          try {
            pending.assertCurrentScope();
          } catch (error) {
            finish(() => reject(error));
            return;
          }
          if (
            prediction.status === CustomModelTrainingStatus.Succeeded &&
            prediction.output?.length
          ) {
            const data = { ok: true, message: "OK", predictionId, output: prediction.output! };
            finish(() => {
              void pending.confirm(data).then(() => resolve(data), reject);
            });
          } else if (
            prediction.status === CustomModelTrainingStatus.Failed ||
            prediction.status === CustomModelTrainingStatus.Canceled
          ) {
            const error = "error" in prediction && prediction.error;
            const failure = new Error(
              typeof error === "string" ? error : "Image generation failed",
            );
            finish(() => {
              void pending.confirm({ ok: true, predictionId, status: prediction.status }).then(
                () => reject(failure),
                () => reject(failure),
              );
            });
          }
        },
      });
      if (settled) {
        unsubscribe();
        return;
      }
      try {
        pending.assertCurrentScope();
      } catch (error) {
        finish(() => reject(error));
        return;
      }

      this.imageGenerationFromDnd(pending.request).then(
        ({ data }) => {
          void pending.confirm(data).then(
            () => {
              if (
                data.ok &&
                "status" in data &&
                (data.status === "starting" || data.status === "processing")
              ) {
                return;
              }
              finish(() => resolve(data));
            },
            (error) => finish(() => reject(error)),
          );
        },
        (error: unknown) => {
          if ((error as { code?: unknown })?.code !== "functions/deadline-exceeded") {
            finish(() => reject(error));
          }
        },
      );
    });
  }
}
